import React, { useCallback, useContext, useState, useEffect } from 'react';

import { Icon } from 'design-system-web';
import cx from 'classnames';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import { ContentRendererContext } from 'lane-shared/contexts';
import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { useVisitorsList, useFlag } from 'lane-shared/hooks';
import useChannelProfileQuery from 'lane-shared/hooks/useChannelProfileQuery';
import { FeatureFlag } from 'constants-flags';
import {
  ContentType,
  VisitorManagementFeatureProperties,
} from 'application-types';

import { H5 } from 'components/typography';

import history from 'helpers/history';
import { getVisitorManagementFeatures } from 'domains/visitorManagement/types/visitorFormInteraction';
import {
  VisitorInviteForm,
  VisitorManagementVisitorListItems,
} from 'domains/visitorManagement/components';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { BulkVisitorsItem } from 'domains/visitorManagement/components/BulkVisitosItem/BulkVisitorsItem';
import { DownloadVisitorInfoSample } from 'domains/visitorManagement/components/DownloadVisitorInfoSample';
import LeaveConfirmationPrompt from 'domains/visitorManagement/components/LeaveConfirmationPrompt/LeaveConfirmationPrompt';
import { VisitorInviteBulkUpload } from 'domains/visitorManagement/components/VisitorInviteBulkUpload/VisitorInviteBulkUpload';

import styles from 'domains/visitorManagement/content-blocks/AddVisitorsBlock.scss';

import { VisitorInviteBulkUploadLegacy } from 'domains/visitorManagement/components/VisitorInviteBulkUpload/VisitorInviteBulkUploadLegacy';
import { useFlatfileUpgradeToPlatformEnabled } from 'react-hooks';
import { UploadFileWrapper } from 'domains/visitorManagement/components/VisitorInviteBulkUpload/UploadFileWrapper';
import { useFormCustomFieldColumns } from 'domains/visitorManagement/hooks/useFormCustomFieldColumns';
import { convertToUUID } from 'uuid-encoding';

function safeConvertToUUID(id?: string | null) {
  if (!id) {
    return undefined;
  }

  try {
    return convertToUUID(id);
  } catch {
    return undefined;
  }
}

export default function AddVisitorsBlock({
  className,
  onChange,
  properties,
  value,
  style,
  show,
  isPreview = false,
  content,
  submissionCompletedAt,
  submitAttempted,
  hostTenantId,
}: {
  className?: string;
  onChange: (value: VisitorType[]) => void;
  properties: VisitorManagementFeatureProperties;
  value: VisitorType[] | null;
  style?: React.CSSProperties;
  show: boolean;
  isPreview?: boolean;
  content?: ContentType;
  submissionCompletedAt?: Date;
  submitAttempted?: boolean;
  hostTenantId?: string;
}) {
  const { t } = useTranslation();

  const [isPristine, setIsPristine] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const { onInteractionUpdated, interaction } = useContext(ContentRendererContext);
  const [existingBulkVisitors, setExistingBulkVisitors] = useState(
    [] as VisitorType[]
  );

  const isBulkImportEnabled =
    properties.bulkImportSetting === undefined
      ? true
      : properties.bulkImportSetting;

  function onBulkVisitorsChange(bulkVisitors: VisitorType[]) {
    setExistingBulkVisitors(bulkVisitors);
  }

  const {
    visitors,
    handleAdd,
    handleBulkAdd: handleBulkAddFromList,
    handleEditSave,
    handleDelete,
    handleBulkDelete,
    resetForm,
  } = useVisitorsList({
    visitors: value,
    bulkVisitors: existingBulkVisitors,
    onBulkVisitorsChange,
    onChange,
    contentType: content?.type,
    channelId: content?.channel?._id,
  });

  const handleBulkAdd = useCallback(
    (bulkVisitors: VisitorType[]) => {
      handleBulkAddFromList(bulkVisitors);
      onInteractionUpdated?.({
        features: {
          VisitorManagement: { uploadedViaBulkImport: true },
        },
      } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
    },
    [handleBulkAddFromList, onInteractionUpdated]
  );

  const visitorInviteBulkUploadFlag = useFlag(
    FeatureFlag.VisitorInviteBulkUpload,
    false
  );

  const flatfileUpgradeToPlatformEnabled =
    useFlatfileUpgradeToPlatformEnabled();

  const visitorManagementCustomFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );

  // Channel admin context only exists under pages/portal/admin/channel; outside that tree
  // useContext returns the default (channel: undefined). Resolve org id from content first,
  // then profile query (slug when _id is missing—same idea as PropertyInputBlock), then admin
  // context for draft flows where channel is not yet on the content object.
  const { channel: channelFromProfileQuery } = useChannelProfileQuery({
    // Avoid a redundant profile request when content already has channel _id.
    channelId: undefined,
    channelSlug: content?.channel?._id ? undefined : content?.channel?.slug,
  });
  const channelFromAdmin = useChannelAdminContext()?.channel;
  const channelIdForOrg =
    content?.channel?._id ||
    channelFromProfileQuery?._id ||
    channelFromAdmin?._id;
  const organizationId = channelIdForOrg
    ? safeConvertToUUID(channelIdForOrg)
    : undefined;
  const { formCustomFieldColumns } = useFormCustomFieldColumns(
    organizationId,
    visitorManagementCustomFieldsEnabled
      ? properties.customFieldIds
      : undefined
  );
  const downloadSampleCustomFieldColumns =
    flatfileUpgradeToPlatformEnabled && visitorManagementCustomFieldsEnabled
      ? formCustomFieldColumns
      : undefined;

  useEffect(() => {
    setShowInviteForm(false);
  }, [visitors.length]);

  useEffect(() => {
    resetForm();
  }, [submissionCompletedAt]);

  useEffect(() => {
    const nowEmpty = existingBulkVisitors.length === 0;
    const persistedBulkFlag = Boolean(
      getVisitorManagementFeatures(interaction)?.uploadedViaBulkImport
    );

    if (nowEmpty && persistedBulkFlag) {
      onInteractionUpdated?.({
        features: {
          VisitorManagement: { uploadedViaBulkImport: false },
        },
      } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
    }
  }, [existingBulkVisitors.length, interaction, onInteractionUpdated]);

  if (!show) return null;

  return (
    <>
      <LeaveConfirmationPrompt
        testId="visitorLeaveConfirmation"
        when={!submitAttempted && (visitors.length > 0 || !isPristine)}
        navigate={path => history.push(path)}
      />
      <div className={cx(className, styles.addVisitorsBlock)} style={style}>
        <H5 className={styles.visitorInviteTitle}>
          {t('web.content.feature.visitorManagement.visitorInvite.form.title')}
        </H5>
        <div className={styles.guestListContainer}>
          {Boolean(visitors.length) && (
            <VisitorManagementVisitorListItems
              visitors={visitors}
              properties={properties}
              allowEdit
              onEditSave={handleEditSave}
              onDelete={handleDelete}
            />
          )}
        </div>
        <div className={styles.guestListContainer}>
          {Boolean(existingBulkVisitors.length) && (
            <BulkVisitorsItem
              bulkVisitors={existingBulkVisitors}
              onDelete={handleBulkDelete}
            />
          )}
        </div>
        <br />
        {Boolean(visitors.length === 0 || showInviteForm) && (
          <VisitorInviteForm
            onAdd={handleAdd}
            value={visitors}
            properties={properties}
            onFieldChange={(changed: boolean) => setIsPristine(changed)}
            isPreview={isPreview}
            tenantId={hostTenantId}
            channelId={organizationId}
          />
        )}

        <div className={styles.buttons}>
          {Boolean(visitors.length !== 0) && !showInviteForm && (
            <Button
              onClick={() => setShowInviteForm(true)}
              disabled={isPreview}
              className={styles.addButton}
              testId="addAnotherVisitor"
              startIcon={
                <Icon
                  name="plus-circle"
                  testId="deleteIcon"
                  set={ICON_SET_FONTAWESOME}
                />
              }
            >
              {t(
                'web.content.feature.visitorManagement.visitorInvite.form.addAnother'
              )}
            </Button>
          )}
          {isBulkImportEnabled &&
          (visitorInviteBulkUploadFlag || Boolean(visitors.length !== 0)) ? (
            <div className={styles.bulkUploadSection}>
              {flatfileUpgradeToPlatformEnabled ? (
                <UploadFileWrapper>
                  <VisitorInviteBulkUpload
                    properties={properties}
                    handleBulkAdd={handleBulkAdd}
                    isPreview={isPreview}
                    formCustomFieldColumns={formCustomFieldColumns}
                  />
                </UploadFileWrapper>
              ) : (
                <VisitorInviteBulkUploadLegacy
                  handleBulkAdd={handleBulkAdd}
                  properties={properties}
                  isPreview={isPreview}
                />
              )}

              <DownloadVisitorInfoSample
                isPreview={isPreview}
                formCustomFieldColumns={downloadSampleCustomFieldColumns}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
